import { ChangeDetectionStrategy, Component, input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssessmentRowComponent } from '../assessment-row/assessment-row.component';
import { EmrInputComponent } from "../../../../shared/emr-input/emr-input.component";
import { EmrCheckboxGroupComponent } from "../../../../shared/emr-checkbox-group/emr-checkbox-group.component";
import { EmrCheckboxComponent } from "../../../../shared/emr-checkbox/emr-checkbox.component";
import { WnlManagerService } from '../wnl-manager.service';
import { FormOption } from 'src/app/shared/models/form-option.model';
import { MusculoskeletalFormGroup } from 'src/app/calvin-emr-shared/models/assessments/musculoskeletal.model';

@Component({
  selector: 'app-musculoskeletal-form',
  templateUrl: './musculoskeletal-form.component.html',
  styleUrls: ['./musculoskeletal-form.component.scss', '../assessments-tab.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    AssessmentRowComponent,
    EmrInputComponent,
    EmrCheckboxGroupComponent,
    EmrCheckboxComponent,
  ],
})
export class MusculoskeletalFormComponent implements OnInit {

  formGroup = input.required<MusculoskeletalFormGroup>();
  wnlManager = new WnlManagerService();

  constructor() {
    this.reset();
  }

  ngOnInit(): void {
    // Add listener to changes in the fall risk form, which updates
    //  fall score each time a change occurs
    this.formGroup().valueChanges.subscribe(() => {
      this.formGroup().controls.fallScore.setValue(this.computeFallRiskScore(),
        { emitEvent: false });    // don't trigger the subscribe we are in!
    });
  }

  reset() {
    this.wnlManager.setControls(
      [
        { control: 'mobilityAndTransfers', default: 'No assists', wnl: false },
        { control: 'assistiveDevices', default: [], wnl: false },
      ]
    );
  }

  get mobilityAndTransfersDefault() {
    return this.wnlManager.controlColl['mobilityAndTransfers'].default;
  }

  get assistiveDevicesDefault() {
    return this.wnlManager.controlColl['assistiveDevices'].default;
  }

  /**
   * Fall Risk computations
   */
  computeAgeScore() {
    // if (!this.formGroup().controls.fallRiskAge) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskAge.value) {
      case '60 - 69 years (1 point)':
        return 1;
      case '70 - 79 years (2 points)':
        return 2;
      case 'greater than or equal to 80 years (3 points)':
        return 3;
      default:
        return 0;
    }
  }

  computeFallHistoryScore() {
    // if (!this.formGroup().controls.fallHistory) {
    //   return 0;
    // }
    return this.formGroup().controls.fallHistory.value ? 5 : 0;
  }

  computeEbuScore() {
    // if (!this.formGroup().controls.fallRiskEbu) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskEbu.value) {
      case 'Incontinence (2 points)':
        return 2;
      case 'Urgency or frequency (2 points)':
        return 2;
      case 'Urgency/frequency and incontinence (4 points)':
        return 4;
      default:
        return 0;
    }
  }

  computeMedicationsScore() {
    // if (!this.formGroup().controls.fallRiskMedications) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskMedications.value) {
      case 'On 1 high fall risk drug (3 points)':
        return 3;
      case 'On 2 or more high fall risk drugs (5 points)':
        return 5;
      case 'Sedated procedure within past 24 hours (7 points)':
        return 7;
      default:
        return 0;
    }
  }

  computePceScore() {
    // if (!this.formGroup().controls.fallRiskPce) {
    //   return 0;
    // }
    switch (this.formGroup().controls.fallRiskPce.value) {
      case 'One present (1 point)':
        return 1;
      case 'Two present (2 points)':
        return 2;
      case '3 or more present (3 points)':
        return 3;
      default:
        return 0;
    }
  }

  computeMobilityFallRiskScore() {
    let score = 0;
    // if (!this.formGroup().controls.fallRiskMobility) {
    //   return score;
    // }
    const mobilityFallRiskFactors: string[] = this.formGroup().controls.fallRiskMobility.value || [];

    mobilityFallRiskFactors.forEach((factor) => {
      if (
        factor === this.MOBILITY_FALL_RISK_ASSISTANCE ||
        factor === this.MOBILITY_FALL_RISK_GAIT ||
        factor === this.MOBILITY_FALL_RISK_IMPAIRMENT
      ) {
        score += 2;
      }
    });

    return score;
  }

  computeCognitionScore() {
    let score = 0;
    // if (!this.formGroup().controls.fallRiskCognition) {
    //   return score;
    // }
    const cognitionFallRiskFactors: string[] = this.formGroup().controls.fallRiskCognition.value || [];

    cognitionFallRiskFactors.forEach((factor) => {
      if (factor === this.COGNITION_FALL_RISK_AWARENESS) {
        score += 1;
      } else if (factor === this.COGNITION_FALL_RISK_IMPULSIVE) {
        score += 2;
      } else if (factor === this.COGNITION_FALL_RISK_UNDERSTANDING) {
        score += 4;
      }
    });

    return score;
  }

  computeFallRiskScore() {
    return (
      this.computeAgeScore() +
      this.computeFallHistoryScore() +
      this.computeEbuScore() +
      this.computeMedicationsScore() +
      this.computePceScore() +
      this.computeMobilityFallRiskScore() +
      this.computeCognitionScore()
    );
  }

  computeFallRiskCategory(): string {
    if (this.computeFallRiskScore() < 6) {
      return 'Low';
    } else if (this.computeFallRiskScore() >= 6 && this.computeFallRiskScore() <= 13) {
      return 'Medium';
    } else {
      return 'High';
    }
  }

  mobilityAndTransfersOptions = [
    new FormOption('No assists'),
    new FormOption('Standby assist'),
    new FormOption('One assist'),
    new FormOption('Two assists'),
    new FormOption('Max assists'),
  ];

  deviceOptions = [
    new FormOption('Gait Belt'),
    new FormOption('Walker'),
    new FormOption('Cane'),
    new FormOption('Trapeze'),
  ];

  ageOptions = [
    new FormOption('60 - 69 years (1 point)'),
    new FormOption('70 - 79 years (2 points)'),
    new FormOption('greater than or equal to 80 years (3 points)'),
  ];

  fallHistoryOption = 'One fall within 6 months before admission (5 points)';

  ebuOptions = [
    new FormOption('Incontinence (2 points)'),
    new FormOption('Urgency or frequency (2 points)'),
    new FormOption('Urgency/frequency and incontinence (4 points)'),
  ];

  medicationsOptions = [
    new FormOption('On 1 high fall risk drug (3 points)'),
    new FormOption('On 2 or more high fall risk drugs (5 points)'),
    new FormOption('Sedated procedure within past 24 hours (7 points)'),
  ];

  pceOptions = [
    new FormOption('One present (1 point)'),
    new FormOption('Two present (2 points)'),
    new FormOption('3 or more present (3 points)'),
  ];

  MOBILITY_FALL_RISK_ASSISTANCE =
    'Requires assistance or supervision for mobility, transfer, or ambulation (2 points)';
  MOBILITY_FALL_RISK_GAIT = 'Unsteady gait (2 points)';
  MOBILITY_FALL_RISK_IMPAIRMENT =
    'Visual or auditory impairment affecting mobility (2 points)';
  mobilityFallRiskOptions = [
    new FormOption(this.MOBILITY_FALL_RISK_ASSISTANCE),
    new FormOption(this.MOBILITY_FALL_RISK_GAIT),
    new FormOption(this.MOBILITY_FALL_RISK_IMPAIRMENT),
  ];

  COGNITION_FALL_RISK_AWARENESS =
    'Altered awareness of immediate physical environment (1 point)';
  COGNITION_FALL_RISK_IMPULSIVE = 'Impulsive (2 points)';
  COGNITION_FALL_RISK_UNDERSTANDING =
    "Lack of understanding of one's physical and cognitive limitations (4 points)";
  cognitionOptions = [
    new FormOption(this.COGNITION_FALL_RISK_AWARENESS),
    new FormOption(this.COGNITION_FALL_RISK_IMPULSIVE),
    new FormOption(this.COGNITION_FALL_RISK_UNDERSTANDING),
  ];
}
